import React, { useCallback, useEffect, useState } from 'react';
import { procurementService } from '../../../services/api';
import { useEstadoDaCarga, EstadoDaLista } from '../../../design';
import { TONE_BADGE, TONE_BORDER, TONE_BARRA, TONE_TEXT, SEM_BARRA } from '../../../utils/statusColors';
import { Icon } from '../../shared/Icon';
import { trocarParametroNaUrl } from '../../../config/rotas';
import {
  ETAPAS, FonteDoEncontrar, PortalDoRegistro, EtapaDoPortal, PassoDoFluxo, TomDoPasso, VarianteDaEtapa, ComparativoDaEtapa, lerRegistro, rotuloDaCelula, avisoAoLado, tituloDoPasso, tomDaCelula, linhaDaEvidencia, estadoEfetivo, falhaMaisDoQueFunciona,
  lerSemLinha, RotuloSemLinha, tomDoAviso,
} from './capacidades';

/**
 * A MATRIZ DE PORTAIS (aba "Portais" de Licitações → Automação, aprovada pelo Heitor em 15/10/2026) e a página de cada
 * portal, as duas geradas do registro do backend (`GET /procurement/portais/capacidades`). Ver ./capacidades.
 *
 * A página do portal abre por "?portal=<chave>" (replace, como a conta bancária): o link e o F5 voltam nela. Sem a rota,
 * a tela diz que não leu, e nunca mostra a matriz vazia como se nenhum portal tivesse nada.
 */
const portalDaUrl = () => new URLSearchParams(window.location.search).get('portal');

function Celula({ etapa, agora, fontes, todasAsLimitacoes }: { etapa: EtapaDoPortal | undefined; agora: number; fontes?: FonteDoEncontrar[]; todasAsLimitacoes?: boolean }) {
  const tom = tomDaCelula(etapa, agora);
  const ev = linhaDaEvidencia(etapa, { fontes, todasAsLimitacoes });
  const derivado = etapa?.estado === 'operante' && estadoEfetivo(etapa, agora) === 'parado';
  const aoLado = avisoAoLado(etapa, agora);
  const titulo = derivado
    ? 'Declarado como funcionando no código, mas sem nenhum uso nos últimos 30 dias.'
    : !aoLado && etapa?.evidencia?.aviso && etapa.evidencia.avisoDetalhe ? etapa.evidencia.avisoDetalhe
    : falhaMaisDoQueFunciona(etapa) ? 'Funciona, mas falhou mais vezes do que deu certo nos últimos 30 dias.' : undefined;
  const pilula = (tomDaPilula: keyof typeof TONE_BADGE, texto: string, title?: string | null, extra?: Record<string, string>, margem = '') => (
    <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${margem} ${TONE_BADGE[tomDaPilula]}`} title={title || undefined} {...extra}>{texto}</span>
  );
  return (
    <div data-estado={estadoEfetivo(etapa, agora)}>
      {pilula(tom, rotuloDaCelula(etapa, agora), titulo)}
      {aoLado && pilula(tomDoAviso(etapa?.evidencia), aoLado, etapa?.evidencia?.avisoDetalhe, { 'data-aviso-ao-lado': '', 'data-codigo': etapa?.evidencia?.codigo || '' }, 'ml-1 mt-1')}
      {ev && <span className="block mt-1 text-[11px] text-slate-400 tabular-nums">{ev}</span>}
    </div>
  );
}

/** O peso do passo é borda + barra + ícone + rótulo, não só cor (molde da Central). O normal fica sem nada. */
const PESO_DO_PASSO: Record<TomDoPasso, { tom: 'danger' | 'warning'; icone: string; rotulo: string } | null> = {
  normal: null,
  atencao: { tom: 'warning', icone: 'alerta', rotulo: 'atenção' },
  destrutivo: { tom: 'danger', icone: 'ban', rotulo: 'irreversível' },
};

function ListaDePassos({ passos, inicio = 0 }: { passos: PassoDoFluxo[]; inicio?: number }) {
  return (
    <ol className="mt-3 space-y-2">
      {passos.map((p, i) => {
        const peso = PESO_DO_PASSO[p.tom || 'normal'];
        return (
        <li key={i} data-tom-do-passo={p.tom || 'normal'}
          className={`rounded border border-l-4 bg-central-elevado px-3 py-2 ${peso ? `${TONE_BORDER[peso.tom]} ${TONE_BARRA[peso.tom]}` : `border-central-borda ${SEM_BARRA}`}`}>
          <p className="text-sm text-slate-200 flex flex-wrap items-center gap-x-1.5">
            {peso && <span className={TONE_TEXT[peso.tom]}><Icon name={peso.icone} className="w-4 h-4" /></span>}
            <span className="text-slate-500 tabular-nums">{inicio + i + 1}.</span><span className={peso?.tom === 'danger' ? 'font-semibold' : undefined}>{tituloDoPasso(p.titulo)}</span>
            {peso && <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${TONE_BADGE[peso.tom]}`}>{peso.rotulo}</span>}
            {p.ator && <span className="ml-0.5 text-[10px] uppercase tracking-wide text-slate-500">{p.ator === 'robo' ? 'robô' : p.ator}</span>}
          </p>
          {p.detalhes?.map((d) => <p key={d} className="text-xs text-slate-400 mt-0.5">{d}</p>)}
          {p.avisos?.map((a) => <p key={a} className="text-xs text-amber-300 mt-0.5">{a}</p>)}
        </li>
        );
      })}
    </ol>
  );
}

/** As variantes da etapa em abas; os passos da variante continuam a numeração dos comuns. */
function Variantes({ variantes, inicio }: { variantes: VarianteDaEtapa[]; inicio: number }) {
  const [ativa, setAtiva] = useState(variantes[0].chave);
  const v = variantes.find((x) => x.chave === ativa) || variantes[0];
  return (
    <div className="mt-3" data-variantes>
      <div className="flex flex-wrap gap-1.5" role="tablist">
        {variantes.map((x) => (
          <button key={x.chave} type="button" role="tab" aria-selected={x.chave === v.chave} onClick={() => setAtiva(x.chave)}
            className={`px-2.5 py-1 rounded-md border text-xs ${x.chave === v.chave ? 'border-slate-400 bg-central-elevado text-white' : 'border-central-borda text-slate-400 hover:text-white'}`}>
            {x.titulo}{x.subtitulo && <span className="ml-1 text-slate-500">{x.subtitulo}</span>}
          </button>
        ))}
      </div>
      {v.meta && <p className="mt-2 text-xs text-slate-400" data-meta-da-variante>{v.meta}</p>}
      <div data-variante={v.chave}><ListaDePassos passos={v.passos} inicio={inicio} /></div>
    </div>
  );
}

function Comparativo({ comparativo }: { comparativo: ComparativoDaEtapa }) {
  return (
    <div className="mt-3 overflow-x-auto rounded border border-central-borda" data-comparativo>
      <table className="w-full text-xs">
        <thead className="bg-central-cartao text-slate-400">
          <tr><th className="text-left px-2 py-1.5">Aspecto</th>{comparativo.colunas.map((c) => <th key={c} className="text-left px-2 py-1.5">{c}</th>)}</tr>
        </thead>
        <tbody>
          {comparativo.linhas.map((l) => (
            <tr key={l.aspecto} className="border-t border-central-borda">
              <td className="px-2 py-1.5 text-slate-300">{l.aspecto}</td>
              {l.valores.map((v, i) => <td key={i} className="px-2 py-1.5 text-slate-400">{v}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PaginaDoPortal({ portal, agora, onVoltar }: { portal: PortalDoRegistro; agora: number; onVoltar: () => void }) {
  return (
    <div className="space-y-4" data-pagina-do-portal={portal.portal}>
      <button type="button" onClick={onVoltar} className="text-sm text-slate-400 hover:text-white">← Todos os portais</button>
      <h3 className="text-lg font-semibold text-white">{portal.nome}</h3>
      {ETAPAS.map(({ chave, rotulo }) => {
        const e = portal.etapas[chave];
        return (
          <section key={chave} className="rounded-lg border border-central-borda bg-central-cartao p-4" data-etapa={chave}>
            <div className="flex items-start justify-between gap-3">
              <h4 className="text-sm font-semibold text-slate-200">{rotulo}</h4>
              <Celula etapa={e} agora={agora} fontes={chave === 'encontrar' ? portal.fonteDoEncontrar : undefined} />
            </div>
            {e?.limitacoes && e.limitacoes.length > 0 && (
              <ul className="mt-2 text-xs text-slate-400 list-disc pl-4 space-y-0.5">
                {e.limitacoes.map((l) => <li key={l}>{l}</li>)}
              </ul>
            )}
            {e?.passos && e.passos.length > 0 && <ListaDePassos passos={e.passos} />}
            {e?.variantes && e.variantes.length > 0 && <Variantes variantes={e.variantes} inicio={(e.passos || []).length} />}
            {e?.comparativo && e.comparativo.linhas.length > 0 && <Comparativo comparativo={e.comparativo} />}
            {!e?.passos?.length && !e?.variantes?.length && <p className="mt-2 text-xs text-slate-500">Sem passos desenhados no registro.</p>}
          </section>
        );
      })}
      {portal.armadilhas && portal.armadilhas.length > 0 && (
        <section className="rounded-lg border border-central-borda bg-central-cartao p-4" data-armadilhas>
          <h4 className="text-sm font-semibold text-slate-200 mb-2">Armadilhas conhecidas</h4>
          {portal.armadilhas.map((a) => (
            <div key={a.titulo} className="mb-2 text-xs">
              <p className="text-slate-200 font-medium">{a.titulo}</p>
              <p className="text-slate-400">Errado: {a.errado}</p>
              <p className="text-slate-300">Certo: {a.certo}</p>
            </div>
          ))}
        </section>
      )}
    </div>
  );
}

/**
 * Os rótulos de `portalOrigem` FORA da matriz (19/10/2026, backend #486): itens que existem no banco e não aparecem em
 * linha nenhuma. Rota própria, pedida junto com a matriz. ⛔ Falha é "não consegui medir", NUNCA "todos têm linha":
 * erro não é vazio. Recarga que falha mantém a lista que já estava, com a falha dita em cima.
 */
function ForaDaMatriz() {
  const carga = useEstadoDaCarga();
  const rodar = carga.rodar;
  const [rotulos, setRotulos] = useState<RotuloSemLinha[] | null>(null);
  const carregar = useCallback(async () => {
    await rodar(async () => { setRotulos(lerSemLinha(await procurementService.getPortaisSemLinha())); });
  }, [rodar]);
  useEffect(() => { carregar(); }, [carregar]);

  const itens = (rotulos || []).reduce((s, r) => s + r.itens, 0);
  return (
    <section className="mt-4 rounded-lg border border-central-borda bg-central-cartao p-4" data-fora-da-matriz>
      <h4 className="text-sm font-semibold text-slate-200">Portais fora da matriz</h4>
      {carga.estado === 'carregando' && !rotulos && <p className="mt-1 text-xs text-slate-400">Medindo os itens de portais sem linha na matriz…</p>}
      {carga.estado === 'erro' && (
        <div role="alert" data-sem-linha-erro className={`mt-1 text-xs ${TONE_TEXT.danger}`}>
          Não consegui medir os portais fora da matriz{carga.erro ? ` (${carga.erro})` : ''}. Isso não quer dizer que todos tenham linha.{' '}
          <button type="button" onClick={carregar} className="underline">Tentar de novo</button>
        </div>
      )}
      {rotulos && rotulos.length === 0 && (
        <p data-sem-linha-vazio className="mt-1 text-xs text-slate-400">Todo item do banco tem portal com linha na matriz.</p>
      )}
      {rotulos && rotulos.length > 0 && (
        <>
          <p data-sem-linha-resumo className={`mt-1 text-xs ${TONE_TEXT.warning}`}>
            {rotulos.length} {rotulos.length === 1 ? 'portal' : 'portais'} com {itens.toLocaleString('pt-BR')} {itens === 1 ? 'item' : 'itens'} no banco
            {rotulos.length === 1 ? ' não tem' : ' não têm'} linha na matriz: a tela não diz o que o HUB faz neles.
          </p>
          <ul className="mt-2 flex flex-wrap gap-1.5">
            {rotulos.map((r) => (
              <li key={r.portalOrigem} data-sem-linha={r.portalOrigem} data-codigo={r.codigo}
                className={`px-2 py-0.5 rounded-full text-[11px] ${TONE_BADGE.warning}`}>
                <span className="font-mono">{r.portalOrigem || '(vazio)'}</span> · {r.itens.toLocaleString('pt-BR')} {r.itens === 1 ? 'item' : 'itens'}
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

export default function PortaisMatriz({ agora = Date.now() }: { agora?: number }) {
  const carga = useEstadoDaCarga();
  const rodar = carga.rodar;
  const [portais, setPortais] = useState<PortalDoRegistro[]>([]);
  const [aberto, setAberto] = useState<string | null>(portalDaUrl);

  const carregar = useCallback(async () => {
    await rodar(async () => {
      setPortais(lerRegistro(await procurementService.getCapacidadesDosPortais()));
    });
  }, [rodar]);
  useEffect(() => { carregar(); }, [carregar]);

  const abrir = (chave: string | null) => {
    setAberto(chave);
    trocarParametroNaUrl('portal', chave);
  };

  const doAberto = aberto ? portais.find((p) => p.portal === aberto && !p.semIntegracao) : undefined;
  if (doAberto) return <PaginaDoPortal portal={doAberto} agora={agora} onVoltar={() => abrir(null)} />;

  return (
    <EstadoDaLista carga={carga} temDados={portais.length > 0} oQue="o registro de portais" onTentarDeNovo={carregar}
      vazio={<p className="text-sm text-slate-400">O registro de portais veio vazio.</p>}>
      <div className="overflow-x-auto rounded-lg border border-central-borda" data-matriz-de-portais>
        <table className="w-full min-w-[760px] text-sm">
          <thead className="bg-central-cartao text-[11px] uppercase tracking-wide text-slate-400">
            <tr>
              <th className="text-left px-3 py-2">Portal</th>
              {ETAPAS.map((e) => <th key={e.chave} className="text-left px-3 py-2">{e.rotulo}</th>)}
            </tr>
          </thead>
          <tbody>
            {/* Integrados primeiro; depois os que recebem itens sem integração; a linha sem portal por último. */}
            {[...portais.filter((p) => !p.semIntegracao), ...portais.filter((p) => p.semIntegracao)].map((p) => (
              <tr key={p.portal} className="border-t border-central-borda align-top" data-portal={p.portal}>
                <th className="text-left px-3 py-2.5">
                  {p.semIntegracao
                    ? <span className="font-semibold text-slate-400" title="Recebe itens, sem integração: não há etapas para desenhar.">{p.nome}</span>
                    : <button type="button" onClick={() => abrir(p.portal)} className="font-semibold text-white hover:underline">{p.nome}</button>}
                </th>
                {ETAPAS.map((e) => (
                  <td key={e.chave} className="px-3 py-2.5" data-etapa={e.chave}>
                    <Celula etapa={p.etapas[e.chave]} agora={agora} fontes={e.chave === 'encontrar' ? p.fonteDoEncontrar : undefined} todasAsLimitacoes={p.semIntegracao} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-xs text-slate-500">
        Fonte: o registro de integrações no código do backend, lido agora. Embaixo de cada estado, o uso nos últimos 30
        dias; "funciona" sem uso nesse prazo aparece como parado. Clique no portal para ver as etapas.
      </p>
      <ForaDaMatriz />
    </EstadoDaLista>
  );
}
